import { Trans, t } from "@lingui/macro";
import { useMemo } from "react";

import { BASIS_POINTS_DIVISOR_BIGINT, USD_DECIMALS } from "config/factors";
import { getIcons } from "config/icons";
import { TOKEN_COLOR_MAP, getWhitelistedV1Tokens } from "config/tokens";
import { MLP_PRICE_DECIMALS } from "config/ui";
import { useInfoTokens } from "domain/tokens";
import { bigMath } from "lib/bigmath";
import { MLP_DECIMALS } from "lib/legacy";
import { formatAmount } from "lib/numbers";
import useWallet from "lib/wallets/useWallet";

import InteractivePieChart from "components/InteractivePieChart/InteractivePieChart";
import AssetDropdown from "./AssetDropdown";

export function MlpCard({
  chainId,
  mlpPrice,
  mlpSupply,
  mlpMarketCap,
  adjustedUsdgSupply,
}: {
  chainId: number;
  mlpPrice: bigint;
  mlpSupply: bigint | undefined;
  mlpMarketCap: bigint | undefined;
  adjustedUsdgSupply: bigint;
}) {
  const currentIcons = getIcons(chainId)!;

  const { active, signer } = useWallet();

  const whitelistedTokens = getWhitelistedV1Tokens(chainId);
  const tokenList = whitelistedTokens.filter((t) => !t.isWrapped);

  const { infoTokens } = useInfoTokens(signer, chainId, active, undefined, undefined);

  const { mlpPool, stableMlp, totalMlp } = useMemo(() => {
    let stableMlp = 0;
    let totalMlp = 0;
    const mlpPool = tokenList
      .map((token) => {
        const tokenInfo = infoTokens[token.address];
        if (tokenInfo.usdgAmount !== undefined && adjustedUsdgSupply !== undefined && adjustedUsdgSupply > 0) {
          const currentWeightBps = bigMath.mulDiv(
            tokenInfo.usdgAmount,
            BASIS_POINTS_DIVISOR_BIGINT,
            adjustedUsdgSupply
          );
          if (tokenInfo.isStable) {
            stableMlp += parseFloat(`${formatAmount(currentWeightBps, 2, 2, false)}`);
          }
          totalMlp += parseFloat(`${formatAmount(currentWeightBps, 2, 2, false)}`);
          return {
            fullname: token.name,
            name: token.symbol,
            color: TOKEN_COLOR_MAP[token.symbol ?? "default"] ?? TOKEN_COLOR_MAP.default,
            value: parseFloat(`${formatAmount(currentWeightBps, 2, 2, false)}`),
          };
        }
        return null;
      })
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
      .filter(<T extends unknown>(x: T): x is NonNullable<T> => Boolean(x));

    return { mlpPool, stableMlp, totalMlp };
  }, [adjustedUsdgSupply, infoTokens, tokenList]);

  let stablePercentage = totalMlp > 0 ? ((stableMlp * 100) / totalMlp).toFixed(2) : "0.0";

  return (
    <div className="App-card">
      <div className="stats-block">
        <div className="App-card-title">
          <div className="App-card-title-mark">
            <div className="App-card-title-mark-icon">
              <img src={currentIcons.mlp} width="40" alt="MLP Icon" />
            </div>
            <div className="App-card-title-mark-info">
              <div className="App-card-title-mark-title">MLP</div>
              <div className="App-card-title-mark-subtitle">MLP</div>
            </div>
            <div>
              <AssetDropdown assetSymbol="MLP" />
            </div>
          </div>
        </div>
        <div className="App-card-divider"></div>
        <div className="App-card-content">
          <div className="App-card-row">
            <div className="label">
              <Trans>Price</Trans>
            </div>
            <div>${formatAmount(mlpPrice, USD_DECIMALS, MLP_PRICE_DECIMALS, true)}</div>
          </div>
          <div className="App-card-row">
            <div className="label">
              <Trans>Supply</Trans>
            </div>
            <div>{formatAmount(mlpSupply, MLP_DECIMALS, 0, true)} MLP</div>
          </div>
          <div className="App-card-row">
            <div className="label">
              <Trans>Total Staked</Trans>
            </div>
            <div>${formatAmount(mlpMarketCap, USD_DECIMALS, 0, true)}</div>
          </div>
          <div className="App-card-row">
            <div className="label">
              <Trans>Market Cap</Trans>
            </div>
            <div>${formatAmount(mlpMarketCap, USD_DECIMALS, 0, true)}</div>
          </div>
          <div className="App-card-row">
            <div className="label">
              <Trans>Stablecoin Percentage</Trans>
            </div>
            <div>{stablePercentage}%</div>
          </div>
        </div>
      </div>
      <InteractivePieChart data={mlpPool} label={t`MLP Pool`} />
    </div>
  );
}
